import React, { useState,useEffect,useContext,useRef } from 'react';
import { healthVersion as version } from '../package.json';
import ReactDOM from 'react-dom';
import { IntlProvider } from 'react-intl';
import App from './components/App';
import GlobalStyle from './style/global';
import styled from 'styled-components';
import { messages, getBrowserLocale, resolveSessionLocale, Locale } from './i18n';
import { BrandingProvider } from './components/shared/BrandingContext';
import { BrandingContext } from './components/shared/BrandingContext';
import { BrandThemeProvider } from './components/shared/BrandThemeProvider';
import { getGeo } from './api/geo';

const DESKTOP_BREAKPOINT = 768;

// ---------------------------------------------------------------------------
// Desktop workaround for the BioSense Signal SDK WASM algorithm.
//
// The SDK selects models and code paths based on the platform:
//   • Android  → loads .android.bin (30 fps) models, osType="android"
//   • iOS/macOS → loads .ios.bin    (60 fps) models, osType="ios"
//   • Other    → loads ALL models (android + ios + generic), osType="windows"
//
// When running on a Windows/desktop browser the "Other" path is taken, which
// loads every model file into WASM memory.  The WASM engine is then initialised
// with osType="windows" and fps=15 (DEFAULT_FPS), but the algorithm expects a
// coherent {osType, fps, models} combination.  On desktop this mismatch causes
// an internal WASM crash (error codes 16380256 / 0xF9F160, domain 3000).
//
// Fix: before the SDK reads navigator.userAgent (which happens lazily on first
// use inside each SDK call), spoof the UA to look like an Android phone.  This
// makes the SDK:
//   1. Load only the .android.bin + generic models
//   2. Pass osType="android" to init_engine
//   3. Use 30 fps (via is30fpsEnabled) — matching the android model files
//
// The spoof is done only on non-mobile platforms so real Android/iOS devices
// keep their native code path.
// ---------------------------------------------------------------------------
const _realUA = navigator.userAgent;
// UA regex catches normal mobile browsers; the second condition catches iPads
// on iPadOS 13+ (and any browser with "Request Desktop Site" enabled) which
// send a macOS UA but still expose touch points.
const _isMobileUA =
  /Android|iPhone|iPad|iPod|IEMobile|Opera Mini/i.test(_realUA) ||
  (navigator.maxTouchPoints > 1 && /Macintosh/i.test(_realUA));

(window as any).__isMobileDevice = _isMobileUA;

if (!_isMobileUA) {
  // Android 13, Chrome 120 UA — recognised as "Android" by ua-parser-js
  const _androidUA =
    'Mozilla/5.0 (Linux; Android 13; Pixel 7) AppleWebKit/537.36 ' +
    '(KHTML, like Gecko) Chrome/120.0.0.0 Mobile Safari/537.36';
  try {
    Object.defineProperty(navigator, 'userAgent', {
      get: () => _androidUA,
      configurable: true,
    });
  } catch (_e) {
    /* some environments disallow the override — proceed without it */
  }
}

// Enable 30 fps camera + WASM fps to match the Android .android.bin models,
// but only on desktop — iOS devices must run at 60 fps to match their .ios.bin models.
(window as any).biosenseSignalConfig = {
  reportDevErrors: true,
  ...(!_isMobileUA && { is30fpsEnabled: true }),
};

const PageBackground = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: stretch;
  background: transparent;

  @media (min-width: ${DESKTOP_BREAKPOINT + 1}px) {
    background: #d0d8e4;
    align-items: flex-start;
    overflow-y: auto;
  }
`;

const ThemedBackground: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <PageBackground>
    <MobileFrame>
      {children}
    </MobileFrame>
  </PageBackground>
);

const MobileFrame = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: ${p => p.theme.sfondo || 'transparent'};

  /* Desktop: cap width at 768px, centred, no phone frame */
  @media (min-width: ${DESKTOP_BREAKPOINT + 1}px) {
    width: ${DESKTOP_BREAKPOINT}px;
    height: 100vh;
    flex-shrink: 0;
    overflow-y: auto;
    overflow-x: hidden;
  }
`;

export { LocaleContext } from './contexts/LocaleContext';
import { LocaleContext } from './contexts/LocaleContext';

/**
 * Applies the partner's language once the session is known.
 *
 * The browser locale is what the app starts in (see AppWrapper); this only
 * overrides it when the partner said otherwise — JWT claim first, branding
 * default second. See resolveSessionLocale for the precedence itself.
 */
const ApplyPartnerLocale: React.FC = () => {
  const { branding, brandingSource, tokenLang } = useContext(BrandingContext);
  const { setLocale } = useContext(LocaleContext);
  // The token language lands before the branding fetch resolves, so this
  // effect settles in two passes. Remembering what we applied keeps the second
  // pass from overwriting a language the user picked in between.
  const appliedRef = useRef<Locale | null>(null);

  useEffect(() => {
    const resolved = resolveSessionLocale({
      tokenLang,
      brandingLang: branding.defaultLang,
      brandingFromToken: brandingSource === 'token',
    });

    if (!resolved || appliedRef.current === resolved) return;

    appliedRef.current = resolved;
    setLocale(resolved);
  }, [tokenLang, brandingSource, branding.defaultLang, setLocale]);

  return null;
};

const AppWrapper = () => {
  const [locale, setLocale] = useState<Locale>(getBrowserLocale());

  useEffect(() => {
    document.documentElement.dir = locale === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = locale;
  }, [locale]);

  return (
    <LocaleContext.Provider value={{ locale, setLocale }}>
      <IntlProvider locale={locale} messages={messages[locale]}>
        <BrandingProvider>
          <BrandThemeProvider>
            <GlobalStyle />
            <ThemedBackground>
              <ApplyPartnerLocale />
              <App />
            </ThemedBackground>
          </BrandThemeProvider>
        </BrandingProvider>
      </IntlProvider>
    </LocaleContext.Provider>
  );
};

// Warm the geo lookup so scan posts (including the teardown beacon, which
// cannot await) already have it.
getGeo();

console.log(`App version: ${version}`);
ReactDOM.render(<AppWrapper />, document.getElementById('root'));
